import React, { useState } from 'react';
import { Card, Button, Radio, Typography, message, Timeline, Spin } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

interface CardBindingProgressProps {
  onSuccess: () => void;
  onReauthorize: () => void;
  onReturnToReminder: () => void;
}

/**
 * 银行卡绑定进度（对齐登记簿开立进度页面的UI与交互）
 * - 顶部不展示状态徽章/说明，仅保留时间线
 * - 时间线仅展示节点说明，不展示每个节点时间
 * - 处理中：查询后转圈，停留当前页
 * - 失败：最后节点为“绑定失败”，在节点内展示失败原因；隐藏“查询绑定进度”按钮；提供“返回重新绑卡”
 * - 成功：查询成功后触发 onSuccess（由父组件弹框提示并跳转）
 */
const CardBindingProgress: React.FC<CardBindingProgressProps> = ({
  onSuccess,
  onReauthorize,
  onReturnToReminder,
}) => {
  // 进度状态：pending=处理中, success=成功, failed=失败
  const [status, setStatus] = useState<'pending' | 'success' | 'failed'>('pending');
  // 时间线位置：0初始(未展示)，1已提交，2处理中，3结果
  const [timelineCurrent, setTimelineCurrent] = useState(0);
  // 演示用状态切换（与登记簿页一致的右上角切换）
  const [selectedStatus, setSelectedStatus] = useState<'pending' | 'success' | 'failed'>('pending');
  // 查询loading
  const [processing, setProcessing] = useState(false);

  const failureReasons = ['银行卡信息与银行系统记录不符', '打款金额与指定金额不一致'];

  const handleViewProgress = async () => {
    setProcessing(true);
    // 进入时间线：提交 -> 处理中/结果
    setTimelineCurrent(1); // 提交
    await new Promise((resolve) => setTimeout(resolve, 1200));

    if (selectedStatus === 'pending') {
      setStatus('pending');
      setTimelineCurrent(2); // 处理中
      setProcessing(false);
      message.info('已获取最新进度：处理中');
      return;
    }

    if (selectedStatus === 'failed') {
      setStatus('failed');
      setTimelineCurrent(3); // 结果-失败
      setProcessing(false);
      return;
    }

    // success
    setStatus('success');
    setTimelineCurrent(3); // 结果-成功
    setProcessing(false);
    // 由父组件统一弹框并跳转
    setTimeout(() => onSuccess(), 300);
  };

  const handleBackToModify = () => {
    // 返回重新绑卡（回到上一步表单）
    setTimelineCurrent(0);
    setStatus('pending');
    onReauthorize();
  };

  // 时间线节点定义（对齐登记簿风格）
  const timelineItems = [
    {
      dot: timelineCurrent >= 1 ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <ClockCircleOutlined />,
      color: timelineCurrent >= 1 ? 'green' : 'blue',
      children: (
        <>
          <Text strong>提交绑定信息</Text>
          <Text type="secondary" style={{ display: 'block' }}>
            银行卡信息已提交
          </Text>
        </>
      ),
    },
    {
      dot: timelineCurrent >= 2 ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : <ClockCircleOutlined />,
      color: timelineCurrent >= 2 ? 'green' : 'blue',
      children: (
        <>
          <Text strong>银行验证处理</Text>
          <Text type="secondary" style={{ display: 'block' }}>
            银行正在验证您的银行卡信息
          </Text>
        </>
      ),
    },
    {
      dot: timelineCurrent >= 3 ? (
        status === 'failed' ? (
          <ClockCircleOutlined style={{ color: '#ff4d4f' }} />
        ) : (
          <CheckCircleOutlined style={{ color: '#52c41a' }} />
        )
      ) : (
        <ClockCircleOutlined />
      ),
      color: status === 'failed' ? 'red' : timelineCurrent >= 3 ? 'green' : 'blue',
      children: (
        <>
          <Text strong>{status === 'success' ? '绑定成功' : status === 'failed' ? '绑定失败' : '绑定结果等待通知'}</Text>
          <Text type="secondary" style={{ display: 'block' }}>
            {status === 'success' ? '您的银行卡已经成功绑定' : status === 'failed' ? '您的银行卡绑定未能通过验证' : '等待最终验证结果'}
          </Text>

          {status === 'failed' && (
            <div style={{ marginTop: 8 }}>
              <Text style={{ display: 'block', marginBottom: 4, color: '#ff4d4f' }}>失败原因：</Text>
              <ul style={{ paddingLeft: 18, margin: 0 }}>
                {failureReasons.map((r, i) => (
                  <li key={i}>{r}</li>
                ))}
              </ul>
            </div>
          )}
        </>
      ),
    },
  ];

  return (
    <Card className="enterprise-step-card enterprise-fade-in" style={{ width: '800px', margin: '24px auto 0' }}>


      <div style={{ padding: 24, position: 'relative' }}>
        {/* 右上角演示状态切换（与登记簿页一致） */}
        <div
          style={{
            position: 'fixed',
            top: 120,
            right: 20,
            zIndex: 1000,
            background: 'rgba(255, 255, 255, 0.9)',
            padding: '12px 16px',
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            border: '1px solid #d9d9d9',
          }}
        >
          <div style={{ marginBottom: 8, fontWeight: 600 }}>状态切换（演示）</div>
          <Radio.Group
            value={selectedStatus}
            onChange={(e: any) => setSelectedStatus(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            size="small"
          >
            <Radio.Button value="pending">处理中</Radio.Button>
            <Radio.Button value="success">成功</Radio.Button>
            <Radio.Button value="failed">失败</Radio.Button>
          </Radio.Group>
        </div>

        <Spin spinning={processing}>
          {/* 时间线 */}
          <div style={{ maxWidth: 700, margin: '24px auto 0' }}>
            <Title level={4} style={{ textAlign: 'center', margin: '0 0 24px' }}>
              绑定进度
            </Title>
            <Text type="secondary" style={{ textAlign: 'center', display: 'block', marginBottom: 24 }}>
              打款验证时间预计3-5分钟，具体以银行处理时间为准
            </Text>
            <Timeline items={timelineItems as any} />
          </div>

          {/* 操作按钮（放在时间线下方） */}
          <div style={{ textAlign: 'center', marginTop: 24 }}>
            {status !== 'failed' && (
              <Button
                type="primary"
                size="large"
                onClick={handleViewProgress}
                icon={<ClockCircleOutlined />}
                loading={processing}
                style={{ minWidth: 160 }}
              >
                查询绑卡进度
              </Button>
            )}
            <Button size="large" style={{ marginLeft: 12, minWidth: 160 }} onClick={onReturnToReminder}>
              返回打款验证
            </Button>
            <Button size="large" style={{ marginLeft: 12, minWidth: 160 }} onClick={() => window.open('about:blank', '_blank')}>
              返回订单中心
            </Button>
            {status === 'failed' && (
              <Button type="primary" size="large" style={{ marginLeft: 12, minWidth: 160 }} onClick={handleBackToModify}>
                返回重新绑卡
              </Button>
            )}
          </div>
        </Spin>
      </div>
    </Card>
  );
};

export default CardBindingProgress;